import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Lock, User, MapPin } from "lucide-react";
import api from "../api.js";
import { FORCE_POINT_UI } from "../lib/forcePointUi.js";
import BrandMark from "../components/BrandMark.jsx";
import { APP_NAME, APP_TAGLINE } from "../lib/brand.js";

const selectClass = "h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50";

// isCentral — свойство самого бэкенда (IS_CENTRAL = !SERVER_URL), для одного и того же
// физического сервера оно никогда не меняется. Кэшируем последнее успешно полученное значение,
// чтобы временный сбой /api/auth/config (например, всплеск рейт-лимита) не откатывал уже знакомое
// устройство обратно в режим "точка" — этот баг реально стрелял на центральном логине.
const IS_CENTRAL_CACHE_KEY = "lastKnownIsCentral";

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [location, setLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [isCentral, setIsCentral] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Waits (briefly) for the first-launch pull of locations/employees from the central server —
    // matters on a freshly installed point-of-sale instance; on an already-synced one this returns
    // almost immediately since the backend flips pullDone early during its own startup.
    const waitForPullSync = async () => {
      const start = Date.now();
      while (Date.now() - start < 8000) {
        try {
          const { pullDone } = await api.getSyncStatus();
          if (pullDone) return;
        } catch {
          return; // endpoint unreachable — don't block the login screen on it
        }
        await new Promise(r => setTimeout(r, 500));
      }
    };

    api.getAuthConfig()
      .then(async ({ isCentral }) => {
        if (cancelled) return;
        localStorage.setItem(IS_CENTRAL_CACHE_KEY, isCentral ? "1" : "0");
        setIsCentral(isCentral);
        setConfigLoaded(true);
        if (!isCentral) {
          setSyncing(true);
          await waitForPullSync();
          if (cancelled) return;
          setSyncing(false);
          api.listLocations().then(setLocations).catch(() => {});
        } else if (FORCE_POINT_UI) {
          // Staging is central (no pull-sync to wait for) but still needs a location picked at
          // login so req.user.location resolves a YClients company_id downstream.
          api.listLocations().then(setLocations).catch(() => {});
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Config endpoint unreachable — use whatever this device last confirmed itself to be
        // (isCentral is a fixed property of the backend, never flips for the same device). Only
        // a device that has NEVER successfully loaded config falls back to the point picker —
        // the safer default for a freshly installed point-of-sale instance.
        const cached = localStorage.getItem(IS_CENTRAL_CACHE_KEY);
        const fallbackIsCentral = cached === "1";
        setIsCentral(fallbackIsCentral);
        setConfigLoaded(true);
        if (!fallbackIsCentral || FORCE_POINT_UI) {
          api.listLocations().then(setLocations).catch(() => {});
        }
      });

    return () => { cancelled = true; };
  }, []);

  const showLocationPicker = !isCentral || FORCE_POINT_UI;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (showLocationPicker && !location) { setError("Выберите точку продажи"); return; }
    setLoading(true);
    try {
      await login(username, password, showLocationPicker ? location : null);
    } catch (err) {
      setError(err.response?.data?.error || "Неверный логин или пароль");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center min-h-screen"
      style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E3A8A 55%, #0F172A 100%)" }}
    >
      {/* Decorative circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute rounded-full opacity-[0.06]" style={{ width: 600, height: 600, background: "#3B82F6", top: -200, right: -100 }} />
        <div className="absolute rounded-full opacity-[0.04]" style={{ width: 400, height: 400, background: "#60A5FA", bottom: -100, left: -80 }} />
      </div>

      <div className="relative w-full" style={{ maxWidth: 400, padding: "0 16px" }}>
        {/* Card */}
        <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>
          {/* Header stripe */}
          <div className="flex flex-col items-center justify-center gap-3 py-8 px-8" style={{ background: "linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)" }}>
            <div className="flex items-center justify-center rounded-2xl bg-white shadow-sm" style={{ width: 64, height: 64 }}>
              <BrandMark className="h-11 w-11" />
            </div>
            <div className="text-center">
              <h1 className="font-extrabold text-white" style={{ fontSize: 24, letterSpacing: "-0.5px" }}>{APP_NAME}</h1>
              <p className="text-blue-200 text-xs mt-1">{APP_TAGLINE}</p>
            </div>
          </div>

          {/* Form */}
          {syncing ? (
            <div className="flex flex-col items-center gap-3 p-8 text-slate-500">
              <div className="h-6 w-6 rounded-full border-2 border-slate-200 border-t-blue-600 animate-spin" />
              <p className="text-sm">Синхронизация с центральным сервером...</p>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-8">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Логин</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" style={{ width: 15, height: 15 }} />
                <Input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Введите логин"
                  autoFocus
                  autoComplete="username"
                  className="pl-9"
                  style={{ borderRadius: 9 }}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Пароль</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" style={{ width: 15, height: 15 }} />
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Введите пароль"
                  autoComplete="current-password"
                  className="pl-9"
                  style={{ borderRadius: 9 }}
                />
              </div>
            </div>

            {configLoaded && showLocationPicker && (
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Точка продажи</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" style={{ width: 15, height: 15 }} />
                  <select
                    className={cn(selectClass, "pl-9")}
                    style={{ borderRadius: 9 }}
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                  >
                    <option value="">Выберите точку...</option>
                    {locations.map(loc => <option key={loc.id} value={loc.name}>{loc.name}</option>)}
                  </select>
                </div>
              </div>
            )}

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full font-bold mt-1"
              style={{ borderRadius: 9, height: 44, fontSize: 15 }}
            >
              {loading ? "Вход..." : "Войти"}
            </Button>
          </form>
          )}
        </div>

        <p className="text-center text-xs mt-5" style={{ color: "rgba(255,255,255,0.3)" }}>
          {APP_NAME} © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
